import { useState, useEffect, useRef } from 'react';

export interface UseReferenceDataOptions<T> {
  loadFn: () => Promise<T[]>;
  enabled?: boolean;
}

export interface UseReferenceDataReturn<T> {
  data: T[];
  loading: boolean;
  error: Error | null;
  reload: () => Promise<void>;
}

/**
 * Custom hook for loading reference data (ports, shippers, consignees, etc.)
 * Handles loading state, error handling, and prevents duplicate loads
 * 
 * @example
 * ```tsx
 * const { data: ports, loading } = useReferenceData({
 *   loadFn: () => portService.getAll({ limit: 1000 })
 * });
 * ```
 */
export function useReferenceData<T>({
  loadFn,
  enabled = true
}: UseReferenceDataOptions<T>): UseReferenceDataReturn<T> {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const isLoadingRef = useRef(false);
  const hasLoadedRef = useRef(false);

  const load = async () => {
    if (isLoadingRef.current || !enabled) return;
    
    try {
      isLoadingRef.current = true;
      setLoading(true);
      setError(null);
      
      const result = await loadFn();
      setData(result);
      hasLoadedRef.current = true;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to load data');
      setError(error);
      console.error('Error loading reference data:', error);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  };

  useEffect(() => {
    if (enabled && !hasLoadedRef.current) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return {
    data,
    loading,
    error,
    reload: load
  };
}

